import React, { useEffect, useMemo, useState } from "react";
import { Box, CssBaseline, Drawer, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppSidebar,
  DRAWER_WIDTH,
  MINI_DRAWER_WIDTH,
  MobileNavDrawer
} from "./AppSidebar.js";
import { LayoutShellContext } from "./layoutShellContext.js";
import { HelpCenter } from "./HelpCenter.js";
import { VersionsDialog } from "./VersionsDialog.js";

interface LayoutProps {
  children: React.ReactNode;
  onLogout?: () => void;
  userName?: string;
  userEmail?: string;
}

export function Layout({ children, onLogout, userName, userEmail }: LayoutProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const shellValue = useMemo(
    () => ({
      isMobile,
      mobileNavOpen: mobileOpen,
      setMobileNavOpen: setMobileOpen,
      desktopCollapsed,
      toggleDesktopNav: () => setDesktopCollapsed((c) => !c)
    }),
    [isMobile, mobileOpen, desktopCollapsed]
  );

  const drawerTransition = theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  });

  return (
    <LayoutShellContext.Provider value={shellValue}>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <CssBaseline />

        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: desktopCollapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH,
              flexShrink: 0,
              transition: drawerTransition,
              "& .MuiDrawer-paper": {
                width: desktopCollapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH,
                boxSizing: "border-box",
                borderRight: "none",
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
                transition: drawerTransition
              }
            }}
          >
            <AppSidebar
              mini={desktopCollapsed}
              currentPath={location.pathname}
              onNavigate={navigate}
              userName={userName}
              userEmail={userEmail}
              onLogout={onLogout}
            />
          </Drawer>
        )}

        {isMobile && (
          <MobileNavDrawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            currentPath={location.pathname}
            onNavigate={navigate}
            userName={userName}
            userEmail={userEmail}
            onLogout={() => {
              onLogout?.();
              setMobileOpen(false);
            }}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: "100vh",
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
            minWidth: 0
          }}
        >
          {children}
        </Box>

        <HelpCenter />
        <VersionsDialog />
      </Box>
    </LayoutShellContext.Provider>
  );
}
